'use client';
import React from 'react';
import { DateTime } from 'luxon';

import Layout from '../lib/components/Layout';
import { Earnings, ISetParams } from '@benzinga/calendar-manager';
import { SessionContext } from '@benzinga/session-context';
import ColumnsDefinition from './ColumnsDef';
import { formatRequestDate } from '../lib/utils';
import { fetchEarnings } from './Services';
import { CALENDAR_FETCH_PAGE_SIZE, useCalendarPagedFetch } from '../lib/useCalendarPagedFetch';

import { usePermission } from '@benzinga/user-context';
import { GatedBlurResolveParams, ICalendarProps, IDateRange } from '../lib/interfaces';
import { resolveEarningsGatedBlur } from './resolveEarningsGatedBlur';

export const EarningsCalendarColumnsDefinition = ColumnsDefinition;
export const fetchEarningsData = fetchEarnings;
export { resolveEarningsGatedBlur } from './resolveEarningsGatedBlur';

export const EarningsCalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  calendarType,
  hasStockTableStyling,
  height,
  hideFilters,
  initialData,
  initialDateRange,
  initialTicker,
  leftSlot,
  onReady,
}) => {
  const session = React.useContext(SessionContext);
  const hasUnlimitedCalendars = usePermission('com/read', 'unlimited-calendars');

  const dateRange: IDateRange = initialDateRange ?? {
    date_from: formatRequestDate(new Date()),
    date_to: formatRequestDate(DateTime.now().plus({ days: 7 }).toJSDate()),
  };

  const initialParams = React.useMemo<ISetParams>(
    () => ({
      dateFrom: dateRange.date_from,
      dateTo: dateRange.date_to,
      ...(initialTicker && { symbols: initialTicker }),
    }),
    [dateRange.date_from, dateRange.date_to, initialTicker],
  );

  const { loading, onFilterChanged, onViewportChanged, setTableData, tableData } = useCalendarPagedFetch({
    fetchPage: (params, page) =>
      fetchEarningsData(session.getSession(), {
        ...(params as ISetParams),
        page,
        pageSize: CALENDAR_FETCH_PAGE_SIZE,
      }),
    initialData,
    initialParams,
  });

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(initialData ?? [], initialParams);
    }
  }, [calendarData?.serverSide, initialData, initialParams, setTableData]);

  function onTableReady() {
    if (initialData) {
      setTableData(initialData, initialParams);
    } else {
      onFilterChanged(initialParams);
    }

    if (onReady) {
      setTimeout(() => onReady());
    }
  }

  const resolveGatedBlur = React.useCallback(
    (params: GatedBlurResolveParams) => resolveEarningsGatedBlur({ ...params, hasUnlimitedCalendars }),
    [hasUnlimitedCalendars],
  );

  return (
    <Layout
      calendar={calendar}
      calendarData={calendarData}
      calendarType={calendarType}
      columnDefs={ColumnsDefinition}
      enableSearch
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      hideFilters={hideFilters}
      initialDateRange={dateRange}
      isGated={true}
      leftSlot={leftSlot}
      onFilterChanged={onFilterChanged}
      onTableReady={onTableReady}
      resolveGatedBlur={resolveGatedBlur}
      tableData={tableData}
      tableLoading={loading}
      theme="modern"
      ticker={initialTicker}
      viewportChange={onViewportChanged}
    />
  );
};
